import { z } from 'zod';
import { action, makeObservable, observable } from 'src_common/common/mobx-wrapper';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { ReportFormState } from 'src/components/Reports/ReportsFilter/ReportForm.state';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';

import * as query from 'src/modules/reports/actions/query.js';
import * as run from 'src/modules/reports/actions/run.js';
import { DateTime } from 'src_common/utils/time/time';

const STATE_KEY = 'reports';
export const ReportKeySchema = z.enum([
    'active-accounts',
    'betting-levy',
    'bet-referral',
    'birthday-list',
    'bonuses',
    'client-openings',
    'freebets',
    'ggy-jurisdiction',
    'gross-gambling-yield',
    'in-play-returns',
    'mobile-returns',
    'ringfenced-funds',
    'statements',
    'trading',
    'card-expiration',
    'payments',
    'winners-and-losers',
    'credit-debit',
    'incomeaccess-accounts',
    'incomeaccess-sales',
    'affiliate-accounts',
    'affiliate-sales',
    'void-bets',
    'play-break',
]);

export type ReportKeyType = z.infer<typeof ReportKeySchema>;

export const REPORT_TYPES: Record<ReportKeyType, string> = {
    'active-accounts': 'Active Accounts',
    'betting-levy': 'Betting Levy',
    'bet-referral': 'Bet Referral',
    'birthday-list': 'Birthday List',
    bonuses: 'Bonuses',
    'client-openings': 'Client Openings',
    freebets: 'Freebets',
    'ggy-jurisdiction': 'GGY based on Jurisdiction',
    'gross-gambling-yield': 'Gross Gambling Yield',
    'in-play-returns': 'In Play Betting Returns',
    'mobile-returns': 'Mobile Returns',
    'ringfenced-funds': 'Ringfenced Funds',
    statements: 'Statements',
    trading: 'Trading',
    'card-expiration': 'Card Expiration',
    payments: 'Payments',
    'winners-and-losers': 'Lifetime Losers & Winners',
    'credit-debit': 'Credit Debit',
    'incomeaccess-accounts': 'IncomeAccess Accounts',
    'incomeaccess-sales': 'IncomeAccess Sales',
    'affiliate-accounts': 'Affiliate Accounts',
    'affiliate-sales': 'Affiliate Sales',
    'void-bets': 'Void Bet',
    'play-break': 'People on player breaks',
};

export class ReportsFilterV2State {
    @observable public currentReportType: ReportKeyType | '' = '';
    @observable public displayReportParamsPopup: boolean = false;
    @observable public includeLastDayChecked: boolean = false;
    @observable public includeLastDayGrantedChecked: boolean = false;
    public accountIdInput: FormInputState<string, string>;

    @observable public isValidReportType: boolean | null = null;
    @observable public isValidAccountIdInput: boolean | null = null;

    public readonly reportFormState: ReportFormState;

    public static get = AutoWeakMap.create((_common) => new ReportsFilterV2State());

    private constructor() {
        this.reportFormState = new ReportFormState();

        this.accountIdInput = FormInputState.new('');

        makeObservable(this);
    }

    public get reportsReduxStore(): {
        runReport: { state: 'IN_PROGRESS' | 'FINISHED' | 'ERROR' };
        items: Record<string, string>;
        collections: Record<string, string>;
    } {
        const reduxStore = $appState.reduxStore.getStateReactive();
        // @ts-expect-error - TODO: Remove redux store in phase 2 - CORE-974
        return reduxStore.reports.toJS();
    }

    @action public onChangeReportType = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const reportTypeRaw = event.target.value;
        const reportType = ReportKeySchema.safeParse(reportTypeRaw);
        if (reportType.success) {
            this.currentReportType = reportType.data;
            this.isValidReportType = true;
            return;
        }
        console.error('Wrong report type', reportType);
    };

    @action public onChangeAccountIdHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.accountIdInput.setValue(event.target.value);
        this.validate();
    };

    @action public onChangeIncludeLastDayHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.includeLastDayChecked = event.target.checked;
    };

    @action public onChangeIncludeLastDayGarantedHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.includeLastDayGrantedChecked = event.target.checked;
    };

    @action public hideReportParamsForm = (): void => {
        this.displayReportParamsPopup = false;
    };

    @action public showReportParamsForm = (): void => {
        const isValid = this.validate();
        if (isValid === false) {
            return;
        }
        this.displayReportParamsPopup = true;
    };

    @action public runReportButtonHandler = (): void => {
        this.showReportParamsForm();
    };

    @action public validate = (): boolean => {
        if (this.currentReportType === '') {
            this.isValidReportType = false;
            return false;
        }

        if (this.currentReportType === 'statements') {
            if (this.accountIdInput.value.trim() === '') {
                this.isValidAccountIdInput = false;
                return false;
            }
            this.isValidAccountIdInput = true;
        }

        return true;
    };

    @action public onReload = (): void => {
        const type = this.isValidReportType === null ? '' : this.currentReportType;
        const accountId = this.accountIdInput.value;

        this.onClear();
        this.onGetReportsRequest(type, accountId);
    };

    // TODO: Remove this method when we will remove redux store
    @action public onClear = (): void => {
        $appState.reduxStore.dispatch(query.clear(STATE_KEY));
        $appState.reduxStore.dispatch(run.clear());
    };

    public onGetReportsRequest = (type: string, accountId: string): void => {
        $appState.reduxStore.dispatch(query.request(STATE_KEY, type, accountId));
    };

    public onRunReportHandler = (runWithFilter: boolean): void => {
        // is no params run immediately if params get all info from form
        this.hideReportParamsForm();

        const type = this.isValidReportType === null ? '' : this.currentReportType;
        const accountId = this.accountIdInput.value;
        const isValid = this.validate();
        if (isValid === false) {
            return;
        }

        if (runWithFilter) {
            this.onRunReport();
        } else {
            $appState.reduxStore.dispatch(run.request(type, accountId));
        }
    };

    private onRunReport = (): void => {
        const type = this.isValidReportType === null ? '' : this.currentReportType;
        const accountId = this.accountIdInput.value;
        const includeLastDay = this.includeLastDayChecked;

        if (this.currentReportType === 'freebets') {
            const data = this.buildFreebetsData();
            $appState.reduxStore.dispatch(run.request(type, accountId, data));
            return;
        }

        const data = {
            startDate: DateTime.from(this.reportFormState.dateStartForm.value)?.toISOString(),
            endDate: includeLastDay
                ? DateTime.from(this.reportFormState.dateEndForm.value)?.addDays(1).toISOString()
                : DateTime.from(this.reportFormState.dateEndForm.value)?.toISOString(),
        };

        if (type.includes('incomeaccess-') || type.includes('affiliate-')) {
            data.startDate = DateTime.from(data.startDate)?.format('YYYY-MM-DD');
            data.endDate = includeLastDay
                ? DateTime.from(data.endDate)?.addDays(1).format('YYYY-MM-DD')
                : DateTime.from(data.endDate)?.format('YYYY-MM-DD');
        }

        if (type.includes('affiliate-')) {
            data.startDate = DateTime.from(data.startDate)?.format('YYYY-MM-DD');
            data.endDate = DateTime.from(data.endDate)?.format('YYYY-MM-DD');
        }

        $appState.reduxStore.dispatch(run.request(type, accountId, data));
    };

    private buildFreebetsData = (): {} => {
        const data: Partial<Record<string, string>> = {};

        const GrantedRange = {
            startDate: this.reportFormState.dateStartForm.value,
            endDate: this.reportFormState.dateEndForm.value,
        };
        const ClaimedRange = {
            startDate: this.reportFormState.claimedStartDateForm.value,
            endDate: this.reportFormState.claimedEndDateForm.value,
        };

        const includeLastDay_Granted = this.includeLastDayGrantedChecked;
        const includeLastDay_Claimed = this.includeLastDayChecked;

        data.grantStartDate = DateTime.from(GrantedRange.startDate)?.toISOString();
        data.grantEndDate = includeLastDay_Granted
            ? DateTime.from(GrantedRange.endDate)?.addDays(1).toISOString()
            : DateTime.from(GrantedRange.endDate)?.toISOString();

        data.claimStartDate = DateTime.from(ClaimedRange.startDate)?.toISOString();
        data.claimEndDate = includeLastDay_Claimed
            ? DateTime.from(ClaimedRange.endDate)?.addDays(1).toISOString()
            : DateTime.from(ClaimedRange.endDate)?.toISOString();

        return data;
    };
}
